import {
    createContext,
    useContext,
    useState,
    useCallback,
    useMemo,
    ReactNode,
} from "react";
import type {DocumentPartsResponse, InlineRelationsResponse} from "../types";
import {dibAppApi} from "../../../dibAppApi/dibAppApi";

export type PanelFetchType = "documentParts" | "inlineRelations";

export type PanelFetchParams = {
    resourceId?: string;
    segmentId?: string;
    id?: string;
};

export type PanelItemStatus = "loading" | "loaded" | "error";

export type PanelItem = {
    key: string;
    label: string;
    fetchType: PanelFetchType;
    params: PanelFetchParams;
    status: PanelItemStatus;
    data: DocumentPartsResponse | InlineRelationsResponse | null;
    error: string | null;
};

export type OpenPanelRequest = {
    label: string;
    fetchType: PanelFetchType;
    params: PanelFetchParams;
};

export type PanelContextValue = {
    items: PanelItem[];
    activeKey: string | null;
    isOpen: boolean;
    openPanel: (request: OpenPanelRequest) => void;
    closePanel: (key: string) => void;
    closeAll: () => void;
    setActivePanel: (key: string) => void;
};

const buildPanelKey = (
    fetchType: PanelFetchType,
    params: PanelFetchParams
): string =>
    `${fetchType}::${params.resourceId ?? ""}::${params.segmentId ?? ""}::${params.id ?? ""}`;

export const PanelContext = createContext<PanelContextValue | null>(null);

export const PanelProvider = ({children}: {children: ReactNode}) => {
    const [items, setItems] = useState<PanelItem[]>([]);
    const [activeKey, setActiveKey] = useState<string | null>(null);

    const [triggerGetDocumentParts] =
        dibAppApi.endpoints.getDocumentParts.useLazyQuery();
    const [triggerGetInlineRelations] =
        dibAppApi.endpoints.getInlineRelations.useLazyQuery();

    const openPanel = useCallback(
        (request: OpenPanelRequest) => {
            const key = buildPanelKey(request.fetchType, request.params);

            setItems((prev) => {
                const existing = prev.find((item) => item.key === key);
                if (existing) {
                    setActiveKey(key);
                    return prev;
                }

                const newItem: PanelItem = {
                    key,
                    label: request.label,
                    fetchType: request.fetchType,
                    params: request.params,
                    status: "loading",
                    data: null,
                    error: null,
                };

                setActiveKey(key);

                const trigger =
                    request.fetchType === "documentParts"
                        ? triggerGetDocumentParts({
                              resourceId: request.params.resourceId ?? "",
                              segmentId: request.params.segmentId ?? "",
                              id: request.params.id ?? "",
                          })
                        : triggerGetInlineRelations({
                              resourceId: request.params.resourceId ?? "",
                              id: request.params.id ?? "",
                          });

                trigger.then((result) => {
                    setItems((current) =>
                        current.map((item) =>
                            item.key === key
                                ? {
                                      ...item,
                                      status: result.error ? "error" : "loaded",
                                      data: result.data ?? null,
                                      error: result.error
                                          ? String(result.error)
                                          : null,
                                  }
                                : item
                        )
                    );
                });

                return [...prev, newItem];
            });
        },
        [triggerGetDocumentParts, triggerGetInlineRelations]
    );

    const closePanel = useCallback(
        (key: string) => {
            setItems((prev) => {
                const filtered = prev.filter((item) => item.key !== key);
                if (activeKey === key) {
                    setActiveKey(
                        filtered.length > 0
                            ? filtered[filtered.length - 1].key
                            : null
                    );
                }
                return filtered;
            });
        },
        [activeKey]
    );

    const closeAll = useCallback(() => {
        setItems([]);
        setActiveKey(null);
    }, []);

    const setActivePanelHandler = useCallback((key: string) => {
        setActiveKey(key);
    }, []);

    const value = useMemo<PanelContextValue>(
        () => ({
            items,
            activeKey,
            isOpen: items.length > 0,
            openPanel,
            closePanel,
            closeAll,
            setActivePanel: setActivePanelHandler,
        }),
        [
            items,
            activeKey,
            openPanel,
            closePanel,
            closeAll,
            setActivePanelHandler,
        ]
    );

    return (
        <PanelContext.Provider value={value}>{children}</PanelContext.Provider>
    );
};

export const usePanel = () => {
    const context = useContext(PanelContext);
    if (!context) {
        throw new Error("usePanel must be used within a PanelProvider");
    }
    return context;
};
